'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { currencies } from '@/lib/currency-data';
import { browserGuestCurrencyStorage, readGuestCurrency } from '@/lib/guest-currency';
import { groupTimezonesByOffset, getTimezoneOffsetLabel } from '@/lib/timezones';
import { useAuth } from '@/context/auth-context';
import { useTheme } from '@/context/theme-context';

type DateFormat = 'DD/MM/YYYY' | 'YYYY/MM/DD' | 'MM/DD/YYYY';

/**
 * One-time, first-sign-in dialog that surfaces the currency / timezone / date
 * format the server detected for a brand-new account and lets the user keep or
 * change them. Detection itself happens server-side (createVerifiedUserProfile)
 * and client-side (browser locale); this component only confirms it, except
 * that a currency the visitor picked while signed out is offered in place of
 * the detected one (see `guestPick` below). Language is intentionally not
 * shown here: it stays on auto-detect until the user changes it in Settings.
 *
 * It renders for a signed-in user whose `localePrefsConfirmed` flag is exactly
 * `false` (stamped on new profiles only). On any close, including dismissal, it
 * persists the shown selections and flips the flag to `true` so it never
 * reappears. Existing accounts have no flag and are never prompted.
 *
 * ClientLayout mounts it on every page, so this outer check is all that runs
 * for everyone else: the form, and above all its time-zone list, are built
 * only while it is open. groupTimezonesByOffset makes an Intl.DateTimeFormat
 * for each of about 400 zones; built on every render, it took 1.5 s of a
 * phone-speed main thread on every page load (measured 2026-10-22).
 */
export function PreferencesConfirmDialog() {
  const { user } = useAuth();
  if (!user || user.localePrefsConfirmed !== false) return null;
  return <PreferencesConfirmForm />;
}

function PreferencesConfirmForm() {
  const { user, updateUser } = useAuth();
  const { currency, dateFormat, effectiveTimezone } = useTheme();

  const open = !!user && user.localePrefsConfirmed === false;

  // A currency picked on this browser while signed out, before the account
  // existed, beats the one detected for it. The profile's currency is resolved
  // from the request IP first (resolveCurrency in
  // functions/src/lib/currency-detect.ts), which outranks any hint the client
  // sends, so without this a visitor who picked EUR on an Australian IP was
  // shown AUD here and, since every close saves what is shown, kept it.
  // Offered rather than imposed: it is on screen to keep or change, and nothing
  // is saved until the dialog closes. Read once per opening, through the same
  // validating reader as the guest currency itself (src/lib/guest-currency.ts).
  const guestPick = useMemo(
    () => (open ? readGuestCurrency(browserGuestCurrencyStorage()) : null),
    [open],
  );

  // Otherwise clamp the detected currency to the options we actually offer so
  // the Select shows a real selection instead of an empty placeholder on an
  // unsupported guess.
  const initialCurrency = guestPick
    ?? (currencies.some((c) => c.code === currency) ? currency : 'USD');

  const [cur, setCur] = useState(initialCurrency);
  const [df, setDf] = useState<DateFormat>(dateFormat);
  const [tz, setTz] = useState(effectiveTimezone);
  const [saving, setSaving] = useState(false);

  // Detected values can resolve after first mount (currency from the user doc,
  // timezone from the browser), so re-seed the form when the dialog becomes
  // relevant.
  useEffect(() => {
    if (!open) return;
    setCur(initialCurrency);
    setDf(dateFormat);
    setTz(effectiveTimezone);
  }, [open, initialCurrency, dateFormat, effectiveTimezone]);

  const timezoneGroups = useMemo(() => groupTimezonesByOffset(), []);
  const selectedOffset = useMemo(() => getTimezoneOffsetLabel(tz), [tz]);
  const handleOffsetChange = (offset: string) => {
    const group = timezoneGroups.find((g) => g.offset === offset);
    if (!group || group.zones.length === 0) return;
    setTz(group.zones.includes(tz) ? tz : group.zones[0]);
  };

  const confirm = async () => {
    if (!user || saving) return;
    setSaving(true);
    try {
      // Persist the settings in one write, then clear the flag in a second.
      // updateUser now sends a mixed call (top-level fields plus settings) as
      // ONE updateDoc, so these two could be merged; they are still separate
      // only because nobody has changed this flow since that fix.
      await updateUser({ settings: { currency: cur, dateFormat: df, timezone: tz } });
      await updateUser({ localePrefsConfirmed: true });
    } finally {
      setSaving(false);
    }
  };

  // Dismissing (Esc / outside click / close button) is treated as accepting the
  // shown values: persist them and clear the flag so the dialog does not nag.
  const onOpenChange = (next: boolean) => {
    if (!next && !saving) void confirm();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm your regional settings</DialogTitle>
          <DialogDescription>
            We picked these based on your location and browser. Keep them or change anything. You can
            update these any time in Settings.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="pref-currency">Currency</Label>
            <Select value={cur} onValueChange={setCur}>
              <SelectTrigger id="pref-currency">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {currencies.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    <div className="flex items-center gap-2">
                      {c.symbol && <span className="font-semibold">{c.symbol}</span>}
                      <span>{c.code} - {c.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Prices display in this currency, and it is the currency your purchases are charged in.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pref-timezone">Timezone</Label>
            <Select value={selectedOffset} onValueChange={handleOffsetChange}>
              <SelectTrigger id="pref-timezone">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {timezoneGroups.map((g) => (
                  <SelectItem key={g.offset} value={g.offset}>{g.offset}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Currently using {tz}.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pref-dateformat">Date format</Label>
            <Select value={df} onValueChange={(v) => setDf(v as DateFormat)}>
              <SelectTrigger id="pref-dateformat">
                <SelectValue placeholder="Select date format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="YYYY/MM/DD">YYYY/MM/DD</SelectItem>
                <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={confirm} loading={saving}>
            {saving ? 'Saving…' : 'Looks good'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
